import {
    Form,
    Button,
    Select,
    Result, 
    Skeleton, 
    Alert, 
    message, 
    Upload,
} from "antd";
import { useMemo, useState } from "react";
import { useForm } from "antd/lib/form/Form";
import {
    signularisApi,
    useCreateMetaDocumentMutation,
    useGetMetaDocumentsQuery,
    useGetRecipientsListQuery
} from "../../../store/api/signularisApi";
import { useAppDispatch } from "../../../hooks/redux";
import { CREATE_DOCUMENT_ROUTE, MAIN_PAGE_ROUTE } from "../../../routing/consts";
import { Link } from "react-router-dom";
import { DocTemplateType, Document, GetMetaDocumentsRequest } from "../../../types/types";
import { InboxOutlined, SendOutlined, LinkOutlined } from "@ant-design/icons";
import Dragger from "antd/lib/upload/Dragger";
import style from '../CreateDocument.module.scss';

const { Item } = Form;
const { Option } = Select;

let guid = () => {
    let s4 = () => Math.floor((1 + Math.random()) * 0x100).toString(10).substring(1);
    return s4() + s4() + s4() + s4();
}

function Template(prop : {referenceId?: string, referenceType?:string, documentTitle?: string}) {
    const [uploadFile, setUploadFile] = useState<File>();
    const dispatch = useAppDispatch();
    const [form] = useForm();

    let reqParams: GetMetaDocumentsRequest = {
        type: 'signed',
        skip: 0,
        limit: 450,
        sort: 'newest'
    }

    const {data: docList, isLoading: docListIsLoading, isError: docListIsError} = useGetMetaDocumentsQuery(reqParams)

    // Явно указываем тип Document[], чтобы избежать ошибок undefined
    const docListFiltred: Document[] = useMemo(() => {
        if (docList?.docs && Array.isArray(docList.docs)) {
            return docList.docs.filter(item => item.documentType === 'extractStorage')
        }
        return [];
    }, [docList?.docs]);

    const { data: recipientsList } = useGetRecipientsListQuery()
    const [docname, setDocname] = useState("")

    const [createMetaDocument, {
        isLoading: mutationLoading,
        isSuccess: mutationSuccess,
    }] = useCreateMetaDocumentMutation()

    const onFinish = async (values: any) => {
        const regnum = guid();
        setDocname('Требование-накладная');
        const formData = new FormData();
        
        formData.append("title", `Требование-накладная №${regnum}`);
        formData.append("brief", "Сгенерировано из шаблона");
        formData.append("recipients", values.recipients.join(","));
        formData.append("documentType", 'deliveryNote');

        // Безопасное получение данных из выбранного документа
        const selectedDoc = docListFiltred[values.reference];
        const refType = prop.referenceType || selectedDoc?.documentType || 'default';
        const refId = prop.referenceId || selectedDoc?.id || '';
        
        formData.append("referenceType", refType);
        formData.append("reference", refId);

        if (uploadFile) {
            formData.append("file", uploadFile, uploadFile.name);
        }
        
        createMetaDocument(formData);
        dispatch(signularisApi.util.invalidateTags(['MetaDocument']));
    }

    if (mutationSuccess) return (
        <Result
            status="success"
            title="Накладная создана"
            subTitle="Документ успешно зарегистрирован и отправлен получателям."
            extra={<Link to={MAIN_PAGE_ROUTE + '/' + CREATE_DOCUMENT_ROUTE}><Button type="primary">Создать еще</Button></Link>}
        />
    );

    if (docListIsLoading) return <div style={{padding: '20px'}}><Skeleton active /><Skeleton active /></div>;

    return (
        <div className={style.generatorWrapper}>
            <h2 className={style.title}>Требование-накладная</h2>
            
            <div className={style.alertInfo}>
                Документ создается на основании ранее подписанной «Выписки из склада». 
                Прикрепите скан-копию заполненной накладной в формате PDF.
            </div>

            <Form form={form} onFinish={onFinish} layout="vertical">
                <Item label="Получатели" name="recipients" rules={[{ required: true, message: 'Выберите получателей' }]} >
                    <Select mode="multiple" placeholder="Кому направить накладную?" size="large" allowClear
           filterOption={(input, option) =>
              (option?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
            }>
                         {recipientsList?.map((recipient) => (
                                <Option key={recipient.id} value={recipient.id} label={recipient.fullname}>
                                {recipient.fullname} {!!recipient.position && <>- <span style={{color: 'gray', fontSize: 12}}>{recipient.position}</span></>}
                                </Option>
                            ))}
                    </Select>
                </Item>

                {prop.documentTitle ? (
                    <div style={{ marginBottom: 24, padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, color: '#166534', fontSize: 13, fontWeight: 600 }}>
                        <LinkOutlined /> Основание: {prop.documentTitle}
                    </div>
                ) : (
                    <Item label="Документ-основание" name="reference" rules={[{ required: true, message: 'Выберите выписку' }]}>
                        <Select placeholder="Выберите подписанную выписку из склада..." size="large">
                            {docListFiltred.map((doc, index) => (
                                <Option key={doc.id} value={index}>
                                    {doc.documentTitle} (от {new Date(doc.time).toLocaleDateString()})
                                </Option>
                            ))}
                        </Select>
                    </Item>
                )}

                <Item label="Файл накладной (PDF)" name="file" rules={[{ required: true, message: 'Прикрепите файл' }]}>
                    <Dragger
                        accept='application/pdf'
                        multiple={false}
                        beforeUpload={(file) => {
                            if (file.type !== "application/pdf") {
                                message.error("Только PDF файлы!");
                                return Upload.LIST_IGNORE;
                            }
                            return false;
                        }}
                        onChange={(info) => setUploadFile(info.fileList[0]?.originFileObj)}
                        onRemove={() => setUploadFile(undefined)}
                        style={{ borderRadius: 16, background: '#f8fafc' }}
                    >
                        <p className={style.draggerIcon}><InboxOutlined /></p>
                        <p className="ant-upload-text">Нажмите или перетащите скан накладной</p>
                    </Dragger>
                </Item>

                <Button 
                    type="primary" 
                    htmlType="submit" 
                    loading={mutationLoading}
                    size="large"
                    icon={<SendOutlined style={{ display: 'inline-flex' }} />}
                    style={{ width: '100%', marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                    Создать накладную
                </Button>
            </Form>
        </div>
    )
}

export default Template;
